import React from 'react';
import classnames from 'classnames';
import { InputNumber } from 'antd';
import { useSelector } from 'react-redux';

const DecimalsLimit = 2;

// Keep only digits and a single decimal separator, capped at `DecimalsLimit` decimals.
// Whichever separator the user typed ('.' or ',') is preserved as-is so a comma-decimal
// keyboard reads naturally while typing; `parse` is what turns it into the canonical
// dot-decimal value the app and the API work with.
function sanitize(rawValue) {
  const cleaned = String(rawValue ?? '').replace(/[^\d.,]/g, '');
  const separatorIndex = cleaned.search(/[.,]/);

  if (separatorIndex === -1) {
    return cleaned;
  }

  const decimals = cleaned
    .slice(separatorIndex + 1)
    .replace(/[.,]/g, '')
    .slice(0, DecimalsLimit);

  return `${cleaned.slice(0, separatorIndex)}${cleaned[separatorIndex]}${decimals}`;
}

function parse(rawValue) {
  return sanitize(rawValue).replace(',', '.');
}

// antd only re-renders the displayed text when the *parsed* value changes, so a keystroke
// the parser throws away (a third decimal, a letter, a second separator) would otherwise
// linger on screen while the value silently ignored it. Refuse those keys outright.
function onKeyDown(event) {
  const { altKey, ctrlKey, key, metaKey, target } = event;

  if (key.length > 1 || altKey || ctrlKey || metaKey) {
    return;
  }

  const { selectionEnd, selectionStart, value } = target;
  const next = `${value.slice(0, selectionStart)}${key}${value.slice(selectionEnd)}`;

  if (sanitize(next) !== next) {
    event.preventDefault();
  }
}

function format(value, { input, userTyping }) {
  // While the user is typing, echo back what they actually typed. antd re-derives the
  // displayed text from the (numeric) controlled value on every change, so anything that
  // number can't represent - a just-typed trailing separator, above all - would be wiped
  // before the next keystroke, which is what makes '.' and ',' impossible to enter.
  if (userTyping) {
    return sanitize(input);
  }

  // At rest (mount, blur, an externally set value) show a settled amount instead of the
  // half-typed text: canonical '.' decimal, always two decimals.
  const parsed = parse(value);

  return parsed === '' ? '' : Number(parsed).toFixed(DecimalsLimit);
}

/**
 * Plain numeric amount input with the currency rendered as text in front of it - no
 * locale grouping or currency masking of the value itself, mirroring the same component
 * in the dashboard. Reports its value as a number via `onChange`.
 */
export default function CurrencyInput({ value, onChange, currency, disabled, id, className, placeholder }) {
  const { company } = useSelector((state) => state.company);

  const _currency = currency ?? company?.currency ?? 'EUR';

  return (
    <InputNumber
      value={value}
      onChange={onChange}
      formatter={format}
      parser={parse}
      onKeyDown={onKeyDown}
      step="0.01"
      // The POS terminal's soft keyboard should open on digits, not letters.
      inputMode="decimal"
      disabled={disabled}
      id={id}
      prefix={<span className="border-r border-gray-300 pr-2 mr-2 text-gray-500">{_currency}</span>}
      className={classnames('w-full', className)}
      placeholder={placeholder}
      autoComplete="off"
    />
  );
}
